import { Typography } from '@mui/material';
import { FormSelect } from 'components/common/FormSelect';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  PieLabelRenderProps,
  Label,
  Tooltip,
} from 'recharts';
import {
  useGetIncidentOccuranceSummaryMutation,
  useGetInspectionRatingSummaryMutation,
  useGetObservationAreaSummaryMutation,
} from 'redux/services/dashboard';
import { CHART_SETTING, SUMMARY_TYPES } from './constants';
import './Summary.scss';

export type SummaryProps = {
  isMobile?: boolean;
  projectId: string;
  userId: string;
};

export const Summary: React.FC<SummaryProps> = ({
  isMobile = false,
  projectId,
  userId,
}) => {
  const { t } = useTranslation();
  const [summaryType, setSummaryType] = useState(SUMMARY_TYPES[0]);
  const [data, setData] = useState<any>([]);
  const chartSetting = isMobile ? CHART_SETTING.mobile : CHART_SETTING.desktop;

  const [getInspectionRatingSummary, { data: inspectionData }] =
    useGetInspectionRatingSummaryMutation();
  const [getObervationAreaSummary, { data: observationData }] =
    useGetObservationAreaSummaryMutation();
  const [getIncidentOccurenceSummary, { data: incidentData }] =
    useGetIncidentOccuranceSummaryMutation();

  useEffect(() => {
    if (projectId && userId) {
      if (summaryType.id === SUMMARY_TYPES[0].id) {
        getInspectionRatingSummary({ projectId, userId });
      } else if (summaryType.id === SUMMARY_TYPES[1].id) {
        getObervationAreaSummary({ projectId, userId });
      } else if (summaryType.id === SUMMARY_TYPES[2].id) {
        getIncidentOccurenceSummary({ projectId, userId });
      }
    }
  }, [
    projectId,
    userId,
    summaryType,
    getInspectionRatingSummary,
    getObervationAreaSummary,
    getIncidentOccurenceSummary,
  ]);

  useEffect(() => {
    if (inspectionData) {
      setData(inspectionData);
    }
  }, [inspectionData]);

  useEffect(() => {
    if (observationData) {
      setData(observationData);
    }
  }, [observationData]);

  useEffect(() => {
    if (incidentData) {
      setData(incidentData);
    }
  }, [incidentData]);

  const renderLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    value,
    name,
  }: PieLabelRenderProps) => {
    const RADIAN = Math.PI / 180;
    const radius =
      25 + Number(innerRadius) + (Number(outerRadius) - Number(innerRadius));
    const x =
      Number(cx) + Number(radius) * Math.cos(-Number(midAngle) * RADIAN);
    const y =
      Number(cy) + Number(radius) * Math.sin(-Number(midAngle) * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#000000"
        fontSize={isMobile ? 8 : 12}
        fontWeight={500}
        textAnchor={x > Number(cx) ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {!isNaN(Number(name)) && !isNaN(Number(value))
          ? `${name} Star`
          : `${name} (${value})`}
      </text>
    );
  };

  const renderCenterLabelText = () => {
    if (
      summaryType.id === SUMMARY_TYPES[0].id ||
      summaryType.id === SUMMARY_TYPES[1].id
    ) {
      return 'Inspection';
    } else if (summaryType.id === SUMMARY_TYPES[2].id) {
      return 'Observations';
    } else if (summaryType.id === SUMMARY_TYPES[3].id) {
      return 'Incidents';
    }
    return '';
  };

  const maxValue = Math.max(
    ...(data?.data?.map(({ value }: any) => value) ?? []),
  );

  const handleSelectSummaryType = (summaryType: any) => {
    setSummaryType(summaryType);
  };

  return (
    <div className="summary p-3 w-100">
      <div className="d-flex justify-content-between gap-1 flex-column flex-md-row">
        <Typography className="chart-title" variant="h6">
          {t('label_summary')}
        </Typography>
        <FormSelect
          className="notification__project-select"
          placeholder="Summary Type"
          selectedItem={summaryType}
          items={SUMMARY_TYPES.map((item) => ({
            ...item,
            name: t('by_label_summary', { name: t(item.id) }),
          }))}
          uniqueId="id"
          onSelect={handleSelectSummaryType}
        />
      </div>

      <ResponsiveContainer width="99%" minHeight={220}>
        <PieChart width={chartSetting.width} height={chartSetting.height}>
          <Pie
            data={[{ value: 100 }]}
            innerRadius={chartSetting.ri2}
            outerRadius={chartSetting.ro2}
            fill="#ffffff"
            dataKey="value"
          />
          <Pie
            data={data?.data ?? []}
            innerRadius={chartSetting.ri1}
            outerRadius={chartSetting.ri2}
            fill="#8884d8"
            dataKey="value"
            label={renderLabel}
          >
            {data?.data?.map((entry: any, index: number) => (
              <Cell
                key={`cell-${index}`}
                fill={`rgba(41,84,108,${entry.value / maxValue})`} //max value
              />
            ))}
            <Label
              fill="#214C64"
              fontWeight={'bold'}
              fontSize={24}
              position="centerBottom"
              value={data?.count ?? 0}
            />
            <Label
              fill="#214C64"
              fontWeight={'bold'}
              fontSize={12}
              position="center"
              width={140}
              dy={10}
              value={'(Open + Close)'}
            />
            <Label
              fill="#214C64"
              fontWeight={'bold'}
              fontSize={12}
              position="center"
              width={140}
              dy={24}
              value={renderCenterLabelText()}
            />
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};
